<template>
	<div ref="root" class="_bot">
		<div class="icon relative flex m-c">
			<NavIcon class="active m-t" :name="icon.active" />
			<NavIcon class="aux m-t" :name="icon.aux" aria-hidden="true" />
		</div>
		<div class="message-clip -p-small whitespace-nowrap flex-grow-1">
			<div class="msg relative w-full m-c">
				<div class="active">
					<template v-for="(_, i) in wordSlots" :key="i">
						<span class="m-t">{{ activeWords[i] ?? '' }}&nbsp;</span>
					</template>
				</div>
				<div class="aux" aria-hidden="true">
					<template v-for="(_, i) in wordSlots" :key="i">
						<span class="m-t">{{ auxWords[i] ?? '' }}&nbsp;</span>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useScreen } from 'vue-screen'
import { NavIcon } from '@/components/primitives/NavIcon'
import { BotInstance } from './Bot.instance'
import { createStateTransition } from './Bot.motion'
import { asyncNextTick } from '@/utils/navigation/async'

const props = defineProps({
	animate: { type: Boolean, default: true },
	disableGlobalEvents: { type: Boolean, default: false },
})

const root = ref(null)
const screen = useScreen()
const icon = reactive({
	active: BotInstance.get().icon,
	aux: BotInstance.get().icon,
})
const message = reactive({
	active: BotInstance.get().message,
	aux: BotInstance.get().message,
})

const activeWords = computed(() => message.active.split(' '))
const auxWords = computed(() => message.aux.split(' '))
const wordSlots = computed(() =>
	new Array(Math.max(10, activeWords.value.length, auxWords.value.length)).fill(null),
)

let transition = null
let globalBlurEvent = null

onMounted(() => {
	transition = createStateTransition(getTransitionElements())

	BotInstance.registerComponent({
		onUpdate: update,
		deferTransition: (...args) => transition.defer(...args),
	})

	if (!props.disableGlobalEvents && !screen.touch) {
		globalBlurEvent = BotInstance.events.setup({
			type: 'custom',
			message: 'Please come back...',
			icon: 'face-fear',
			event: (_, { bot }) => {
				let revert = null
				let lastRemarkIndex = -1
				let blurTimestamp = 0
				const remarks = [
					{ message: 'Please come back...', icon: 'face-fear' },
					{ message: 'We see you clicking away!', icon: 'eyes' },
					{ message: "We're waiting...", icon: 'face-sleepy' },
					{ message: 'Take your time', icon: 'face-halo' },
					{ message: 'Sure, click away, idc', icon: 'face-expressionless' },
				]

				function onWindowBlur() {
					revert?.()

					let attempts = 0
					let index = lastRemarkIndex

					while (index === lastRemarkIndex && attempts < 16) {
						index = Math.floor(Math.random() * remarks.length)
						attempts += 1
					}

					revert = bot.set(remarks[index])
					lastRemarkIndex = index
					blurTimestamp = Date.now()
				}

				function onWindowFocus() {
					if (Date.now() - blurTimestamp > 10 * 1000) {
						const revertWelcomeBack = bot.set({
							icon: 'hands-vulcan-salute',
							message: 'Hey! Welcome back!',
						})
						setTimeout(revertWelcomeBack, 2000)
					}

					revert?.()
					revert = null
				}

				window.addEventListener('blur', onWindowBlur)
				window.addEventListener('focus', onWindowFocus)

				return {
					destroy() {
						revert?.()
						window.removeEventListener('blur', onWindowBlur)
						window.removeEventListener('focus', onWindowFocus)
					},
				}
			},
		})
	}
})

onBeforeUnmount(() => {
	if (globalBlurEvent) {
		BotInstance.events.remove(globalBlurEvent.id)
		globalBlurEvent = null
	}

	transition?.killCurrentAnimation(true)
	BotInstance.registerComponent({})
})

async function update({ message: nextMessage, icon: nextIcon }, options = {}) {
	await asyncNextTick()
	const immediate = !props.animate || options.immediate

	transition.request({
		immediate,
		setup: async () => {
			icon.aux = nextIcon
			message.aux = nextMessage
			await asyncNextTick()
			transition.refresh(getTransitionElements())
		},
		complete: async () => {
			icon.active = nextIcon
			message.active = nextMessage
			await asyncNextTick()
			transition.refresh(getTransitionElements())
		},
	})
}

function getTransitionElements() {
	return {
		container: root.value.closest('.bot-container') ?? root.value,
		icon: {
			active: root.value.querySelector('.icon .active'),
			aux: root.value.querySelector('.icon .aux'),
		},
		message: {
			active: root.value.querySelectorAll('.msg .active .m-t'),
			aux: root.value.querySelectorAll('.msg .aux .m-t'),
		},
	}
}

defineExpose({ update })
</script>

<style lang="scss" scoped>
._bot {
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	padding-left: 2px;
	color: #fff;
	overflow: hidden;
}

._bot .icon {
	display: grid;
	width: 24px;
	height: 24px;
	flex-shrink: 0;
}

._bot .icon .active,
._bot .icon .aux {
	grid-column: 1;
	grid-row: 1;
}

._bot .message-clip {
	min-width: 0;
	overflow: hidden;
}

._bot ._icon {
	--size: 24px;
	--color: #fff;
}

._bot .msg {
	display: grid;
	min-width: 0;
}

._bot .msg .active,
._bot .msg .aux {
	grid-column: 1;
	grid-row: 1;
	min-width: 0;
}

._bot .aux.m-t,
._bot .aux .m-t {
	opacity: 0;
}

._bot .msg .m-t {
	color: #fff;
	white-space: pre;
	@include p-small;
}
</style>
